import { isExpired, tokenApplicantId, tokenSub } from './index';
import { debug } from './debug';

export interface StoredTokens {
    accessToken: string;
    refreshToken?: string;
}

/**
 * The token archive behind the multi-applicant swap.
 *
 * A browser can hold sessions for several applicants at once — the parent frame
 * swaps `?id=` without reloading storage — so `accessToken`/`refreshToken` hold
 * the current pair while `accessTokens`/`refreshTokens` keep one per applicant
 * to swap back to.
 *
 * They used to be append-only, which never mattered while a refresh reissued
 * nothing. Now that refreshing rotates, an active session would add two entries
 * a day forever. Only the newest live token per applicant is ever read, so that
 * is all that is kept.
 */

/** isExpired throws on a malformed token; a token we cannot read is one we cannot use. */
export const isTokenUsable = (token: string | null): boolean => {
    if (!token) return false;
    try {
        return !isExpired(token);
    } catch {
        return false;
    }
};

/** `id` may be the applicant's own id (sub/uid claim) or its applicantId (aid claim). */
export const tokenMatchesId = (token: string | null, id: string): boolean => {
    if (!token) return false;
    try {
        return tokenSub(token) === id || tokenApplicantId(token) === id;
    } catch {
        return false;
    }
};

/** The aid claim, or null if the token is missing or unreadable. */
export const applicantIdFromToken = (token: string | null): string | null => {
    if (!token) return null;
    try {
        return tokenApplicantId(token);
    } catch {
        return null;
    }
};

const isUsable = isTokenUsable;

const read = (key: string): string[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) ?? '[]');
        return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [];
    } catch {
        return [];
    }
};

/** Drops expired and unreadable tokens, then keeps only the newest per applicant. */
const prune = (tokens: string[]): string[] => {
    const newestBySub = new Map<string, string>();
    for (const token of tokens) {
        if (!isUsable(token)) continue;
        const sub = tokenSub(token);
        if (!sub) continue;
        newestBySub.set(sub, token); // appended in order, so a later one wins
    }
    return [...newestBySub.values()];
};

const append = (key: string, token: string): void => {
    const before = read(key);
    const after = prune([...before, token]);
    localStorage.setItem(key, JSON.stringify(after));
    if (before.length + 1 !== after.length) {
        debug('tokens', `pruned ${key}: ${before.length + 1} -> ${after.length}`);
    }
};

/** Records a freshly issued pair as current, and in the archive. */
export const storeTokens = (tokens: StoredTokens): void => {
    localStorage.setItem('accessToken', tokens.accessToken);
    append('accessTokens', tokens.accessToken);

    if (tokens.refreshToken) {
        localStorage.setItem('refreshToken', tokens.refreshToken);
        append('refreshTokens', tokens.refreshToken);
    }
};

/**
 * Find a live session for `id`, which may be either the applicant's own id
 * (sub/uid claim) or its applicantId (aid claim), and make it current.
 * Returns the access token, or null when nothing stored matches.
 */
export const activateStoredSession = (id: string): string | null => {
    const accessToken = read('accessTokens').find((t) => tokenMatchesId(t, id) && isUsable(t)) ?? null;
    if (!accessToken) return null;

    localStorage.setItem('accessToken', accessToken);
    const sub = tokenSub(accessToken);
    const refreshToken = read('refreshTokens').find((t) => tokenSub(t) === sub && isUsable(t)) ?? null;
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

    debug('tokens', `activated a stored session for ${id}`, { hasRefreshToken: Boolean(refreshToken) });
    return accessToken;
};
